import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../layouts/PageTitleContext';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faCapsules,
  faCheck,
  faHospitalUser,
  faUserEdit,
  faNotesMedical,
  faUser,
  faMoneyCheck,
  faCoins,
  faGift,
  faMapMarkerAlt,
  faTrash,
  faFileInvoice,
  faUserSlash,
  faTriangleExclamation,
  faArrowsRotate,
  faNoteSticky,
  faRotateLeft,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import NavTab from '../components/NavTab/NavTab';
import PatientDashboard from '../components/Patient/PatientDashboard';
import PatientInformation from '../components/Patient/PatientInformation';
import PatientAddresses from '../components/Patient/PatientAddresses';
import PatientImage from '../components/Patient/PatientImage';
import PatientOrders from '../components/Patient/PatientOrders';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import PatientLifeLine from '../components/Patient/PatientLifeLine';
import InlineLoader from '../components/Loader/InlineLoader';
import FabGroup from "../components/FabGroup";
import PatientInvoices from '../components/Patient/PatientInvoices';
import PatientPayments from '../components/Patient/PatientPayments';
import PatientReturns from '../components/Patient/PatientReturns';
import PatientNotes from '../components/Patient/PatientNotes';
import NewInvoiceModal from "../components/Modal/NewInvoiceModal";
import PatientAccountEntryModal from '../components/Modal/PatientAccountEntryModal';
import PatientAccount from '../components/Patient/PatientAccount';
import PatientLoyalty from '../components/Loyalty/PatientLoyalty';
import LoyaltyCardGenerator from '../components/Loyalty/LoyaltyCardGenerator';
import Modal from '../components/Modal/Modal';
import PatientAddressModal from '../components/Modal/PatientAddressModal';
import PatientSmartReturnModal from '../components/Modal/PatientSmartReturnModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import { eventBus } from '../bus/eventBus';
import { formatPrice } from '../utils/FormatPrice';

const TAB_SLUGS = {
  patient_dashboard: 'overview',
  patient_information: 'profile',
  patient_addresses: 'addresses',
  patient_orders: 'orders',
  patient_invoices: 'invoices',
  patient_transactions: 'payments',
  patient_account: 'account',
  patient_returns: 'returns',
  patient_notes: 'notes',
  patient_loyalty: 'loyalty',
};

const SLUG_TO_TAB = Object.fromEntries(
  Object.entries(TAB_SLUGS).map(([tabId, slug]) => [slug, tabId])
);

const PatientDetails = () => {
  const { t } = useTranslation();
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { settings, hasRight } = useAuth();
  const canCreateReturn = hasRight('RETURNS_CREATE');
  const loyaltyEnabled = !!settings['loyalty_general_enabled'];
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { setTitle, setTitleAvatar } = usePageTitle();
  const [patientEvents, setPatientEvents] = useState([]);
  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [showAccountEntryModal, setShowAccountEntryModal] = useState(false);
  const [hasLoyaltyCard, setHasLoyaltyCard] = useState(false);
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showSmartReturnModal, setShowSmartReturnModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingPatient, setIsDeletingPatient] = useState(false);
  // Bumped after any action that can change the patient's balance/orders (new invoice,
  // bulk payment...) — forces the tabs that hold their own fetched state (Overview, Invoices)
  // to remount and refetch, instead of showing stale data until a full page reload.
  const [reloadKey, setReloadKey] = useState(0);

  const handleTabChange = (tabId) => {
    const slug = TAB_SLUGS[tabId];
    navigate(tabId === 'patient_dashboard' ? `/patient/${id}` : `/patient/${id}/${slug}`);
  };

  const handleDeletePatient = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeletePatient = async () => {
    setIsDeletingPatient(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deletePatient/${id}`, { withCredentials: true });
      navigate('/patients');
    } catch (err) {
      console.error('Failed to delete patient:', err);
      setIsDeletingPatient(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleNewPatient = () => {
    setShowNewInvoiceModal(true);
  };

  const buttons = [
    //{ label: 'Save Patient', className: 'btn-primary', icon: faCheck, form: "updatePatientForm", isLoading: formLoading, loadingMessage: "Saving Patient ..." },
    { label: t('Delete'), className: 'text-danger', icon: faTrash, onClick: handleDeletePatient }
  ];

  const menuItems = [
    { id: 'patient_dashboard', icon: faTachometerAlt, label: t('Overview'), component: <PatientDashboard key={reloadKey} /> },
    { id: 'patient_information', icon: faUser, label: t('Profile'), component: <PatientInformation /> },
    { id: 'patient_addresses', icon: faMapMarkerAlt, label: t('Adresses'), count: patient?.addresses?.length ?? 0, component: <PatientAddresses /> },
    { id: 'patient_orders', icon:faNotesMedical, label: 'Ventes', count: patient?.ordersCount ?? 0, component: <PatientOrders key={reloadKey} /> },
    { id: 'patient_invoices', icon: faMoneyCheck, label: t('Invoices'), count: patient?.invoicesCount ?? 0, component: <PatientInvoices key={reloadKey} /> },
    { id: 'patient_transactions', icon: faCoins, label: t('Payments'), count: patient?.paymentsCount ?? 0, component: <PatientPayments /> },
    { id: 'patient_returns', icon: faRotateLeft, label: t('Returns'), count: patient?.returnsCount ?? 0, component: <PatientReturns key={reloadKey} /> },
    { id: 'patient_notes', icon: faNoteSticky, label: 'Notes', count: patient?.notesCount ?? 0, component: <PatientNotes /> },
    { id: 'patient_account', icon: faWallet, label: 'Compte', component: <PatientAccount key={reloadKey} onEntryAdded={() => { fetchPatientData(); setReloadKey(prev => prev + 1); }} /> },
    ...(loyaltyEnabled ? [{
      id: 'patient_loyalty',
      icon: faGift,
      label: t('Loyalty'),
      interceptClick: !hasLoyaltyCard,
      onClick: !hasLoyaltyCard ? () => setShowLoyaltyModal(true) : undefined,
      component: <PatientLoyalty />,
    }] : []),
  ];

  const requestedTab = SLUG_TO_TAB[tab];
  const activeTab = menuItems.some(item => item.id === requestedTab) ? requestedTab : 'patient_dashboard';

  const fetchPatientData = useCallback(async ({ showLoader = false } = {}) => {
    try {
      if (showLoader) setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/getPatient/${id}`, {withCredentials: true});
      setPatient(response.data);
      setTitle(`${response.data.firstName} ${response.data.lastName}`);
      setTitleAvatar(<PatientImage patient={response.data} />);
      document.title = `${response.data.firstName}`;
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Erreur lors de la récupération des données du patient.');
      }
      console.error(err);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [id, setTitle, setTitleAvatar]);

  useEffect(() => {
    fetchPatientData({ showLoader: true });
    return () => setTitleAvatar(null);
  }, [fetchPatientData]);

  // photo changed from the overview avatar — refresh the header avatar too
  useEffect(() => {
    const onPhotoUpdated = () => fetchPatientData();
    eventBus.on('patientPhotoUpdated', onPhotoUpdated);
    return () => eventBus.off('patientPhotoUpdated', onPhotoUpdated);
  }, [fetchPatientData]);

  useEffect(() => {
    if (!loyaltyEnabled) return;
    axios.get(`${API_BASE_URL}/api/v1/loyalty-cards/patient/${id}`, { withCredentials: true })
      .then(() => setHasLoyaltyCard(true))
      .catch(() => setHasLoyaltyCard(false));
  }, [id, loyaltyEnabled]);

  const fetchPatientOrders = (patientId) => {
    axios.get(`${API_BASE_URL}/api/v1/getOrders?page=0&size=10&patientId=${patientId}`, {
        withCredentials: true
      })
      .then(response => {
          response.data.content.map((order) => {
            const items = order.items;
            items.map((item) => {
              const orderable = item.orderable;
              setPatientEvents(prevEvents => [
                ...prevEvents,
                {
                  date: order.createdAt,
                  label: orderable.name,
                  status: 'done'
                }
              ]);
            });
          });
        })
        .catch(error => {
          console.error('Failed to fetch orders:', error);
        });
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchPatientOrders(id);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [id]);

  useBreadcrumbSetup([
    { label: "Patients", url: "/patients" },
    { label: patient?.firstName }
  ]);

  if (loading) {
    return <InlineLoader />;
  }

  const errorState = (icon, tint, tintBg, title, description, onRetry) => (
    <div className="page-content">
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', textAlign: 'center', gap: 16,
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: tintBg, color: tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
        }}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{title}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            {description}
          </div>
        </div>
        {onRetry && (
          <button className="btn btn-outline-primary btn-sm" onClick={onRetry}>
            <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
            {t('Actualiser')}
          </button>
        )}
      </div>
    </div>
  );

  if (notFound) {
    return errorState(
      faUserSlash, '#ef4444', '#fef2f2',
      t('Patient introuvable'),
      t('Ce patient n\'existe pas ou a été supprimé.')
    );
  }

  if (error) {
    return errorState(
      faTriangleExclamation, '#d97706', '#fffbeb',
      t('Une erreur est survenue'),
      error,
      () => { setError(null); fetchPatientData({ showLoader: true }); }
    );
  }

  if (!patient) {
    return errorState(
      faUserSlash, '#ef4444', '#fef2f2',
      t('Patient introuvable'),
      t('Aucune donnée patient disponible.'),
      () => fetchPatientData({ showLoader: true })
    );
  }

  if (patient.remainingToPay > 0) {
    buttons.push({ label: t('New invoice'), icon: faFileInvoice, onClick: handleNewPatient });
  }

  buttons.push({
    id: 'add-account-entry',
    label: 'Nouvel ajustement',
    icon: faWallet,
    onClick: () => setShowAccountEntryModal(true),
  });

  buttons.push({
    id: 'add-address',
    label: t('Ajouter une adresse'),
    icon: faMapMarkerAlt,
    onClick: () => setShowAddAddressModal(true),
  });

  if (canCreateReturn) {
    buttons.push({
      id: 'create-return',
      label: t('Create return'),
      icon: faRotateLeft,
      onClick: () => setShowSmartReturnModal(true),
    });
  }

  return (
    <div className="page-content">
      <FabGroup buttons={buttons} />

      <PatientLifeLine events={patientEvents} />

      <NavTab menuItems={menuItems} activeTab={activeTab} onTabChange={handleTabChange} className="navtab--narrow" />

      <NewInvoiceModal
        patientId={id}
        show={showNewInvoiceModal}
        onClose={() => setShowNewInvoiceModal(false)}
        onSuccess={() => { fetchPatientData(); setReloadKey(prev => prev + 1); }}
      />

      <PatientAccountEntryModal
        patientId={id}
        show={showAccountEntryModal}
        onClose={() => setShowAccountEntryModal(false)}
        onSaved={() => { fetchPatientData(); setReloadKey(prev => prev + 1); }}
      />

      <PatientAddressModal
        show={showAddAddressModal}
        onClose={() => setShowAddAddressModal(false)}
        patientId={id}
        address={null}
        onSuccess={() => {
          setShowAddAddressModal(false);
          eventBus.emit('patientAddressesUpdated');
        }}
      />

      <PatientSmartReturnModal
        patientId={id}
        show={showSmartReturnModal}
        onClose={() => setShowSmartReturnModal(false)}
        onSave={() => { fetchPatientData(); setReloadKey(prev => prev + 1); }}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        title={t('Supprimer le patient')}
        description={
          <>
            {t('Êtes-vous sûr de vouloir supprimer ce patient ? Toutes ses données (ventes, factures, paiements, retours, rendez-vous, adresses, fidélité) seront définitivement supprimées. Cette action est irréversible.')}
            {patient.unpaidOrdersCount > 0 && (
              <>
                <br /><br />
                <strong style={{ color: '#991b1b' }}>
                  {t('Attention')} : {t('ce patient a')} {patient.unpaidOrdersCount} {patient.unpaidOrdersCount > 1 ? t('ventes en attente') : t('vente en attente')} ({t('reste dû')} {formatPrice(patient.remainingToPay)}).
                </strong>
              </>
            )}
          </>
        }
        confirmLabel={t('Supprimer')}
        cancelLabel={t('Retour')}
        confirmVariant="danger"
        isLoading={isDeletingPatient}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDeletePatient}
      />

      <Modal
        show={showLoyaltyModal}
        onClose={() => setShowLoyaltyModal(false)}
        title={t("Loyalty program")}
        icon={faGift}
        iconBg="#eff6ff"
        iconColor="#2563eb"
        classSize="modal-dialog-centered"
      >
        <LoyaltyCardGenerator
          patientId={id}
          onCreated={() => {
            setHasLoyaltyCard(true);
            setShowLoyaltyModal(false);
            handleTabChange('patient_loyalty');
          }}
        />
      </Modal>
    </div>
  );
};

export default PatientDetails;
